import { defineStore, acceptHMRUpdate } from "pinia";
import {
  PeopleSearchDTO,
  PersonClient,
  PersonDTO,
  PersonSearchDTO,
  GlobalPeopleSearchDTO,
  BulkAddPermissionsRequest,
  SetPortalPermissionsRequest,
  StaffSavePersonCommand,
  SelfSavePersonCommand,
  ClientAdminSavePersonCommand,
  TenantAdminSavePersonCommand,
} from "./generatedFetchClient";

const personClient = new PersonClient();

export const usePersonStore = defineStore({
  id: "personStore",
  state() {
    return {
      people: [] as PersonSearchDTO[],
      person: null as PersonDTO | null,
      isLoading: false,
      lastSearch: "none",
    };
  },
  actions: {
    setAuthToken(token: string) {
      personClient.setAuthToken(token);
    },
    async getForPortal(portalId: number, force: boolean = false): Promise<PersonSearchDTO[]> {
      try {
        if (this.lastSearch === `getForPortal-${portalId}` && !force) {
          return this.people;
        }
        this.lastSearch = `getForPortal-${portalId}`;
        this.isLoading = true;
        var searchVm = await personClient.getPeopleForPortal(portalId);
        this.people = searchVm.people ?? [];
        this.isLoading = false;
      } catch (apiError: any) {
        console.warn(`Error getting building data for ${portalId}`, apiError);
        this.isLoading = false;
      }
      return [] as PersonSearchDTO[];
    },
    async getPerson(personId: number): Promise<PersonDTO | null> {
      try {
        console.log(` personStore> Loading person ${personId}.`);
        this.isLoading = true;
        var vm = await personClient.getPerson(personId);
        if (vm) {
          this.person = vm.person ?? null;
          this.person?.results?.sort((a, b) => {
            return (b.dateCompleted as any).localeCompare(a.dateCompleted);
          });
          this.person?.loginHistories?.sort((a, b) => {
            return (b.loginTime as any).localeCompare(a.loginTime);
          });
        }
        this.isLoading = false;

        return vm.person ?? null;
      } catch (apiError: any) {
        console.warn(`Error getting person ${personId}`, apiError);
        this.isLoading = false;
      }
      return null;
    },
    async staffSavePerson(person: StaffSavePersonCommand): Promise<string | null> {
      try {
        this.isLoading = true;

        console.log(`Saving person ${person.personId}`, person);
        person.contactEmail =
          person.contactEmail?.length == 0 ? undefined : person.contactEmail?.toString();
        let retCount = await personClient.staffSavePerson(person);

        this.isLoading = false;

        if (retCount > 0) {
          // Remove cache key now that a record is dirty
          this.lastSearch = "dirty";
          //no error means success
          return null;
        }
      } catch (apiError: any) {
        console.warn(`Error saving data for ${person.personId}`, apiError);
        this.isLoading = false;
        return apiError.response ?? apiError.message;
      }
      return "Save unsuccessful";
    },
    async clientAdminSavePerson(person: ClientAdminSavePersonCommand): Promise<string | null> {
      try {
        this.isLoading = true;

        console.log(`Saving person ${person.personId} from client admin`, person);
        person.contactEmail =
          person.contactEmail?.length == 0 ? undefined : person.contactEmail?.toString();
        let retCount = await personClient.clientAdminSavePerson(person.portalId, person);

        this.isLoading = false;

        if (retCount > 0) {
          // Remove cache key now that a record is dirty
          this.lastSearch = "dirty";
          //no error means success
          return null;
        }
      } catch (apiError: any) {
        console.warn(`Error saving data for ${person.personId}`, apiError);
        this.isLoading = false;
        return apiError.response ?? apiError.message;
      }
      return "Save unsuccessful";
    },
    async tenantAdminSavePerson(person: TenantAdminSavePersonCommand): Promise<string | null> {
      try {
        this.isLoading = true;

        console.log(`Saving person ${person.personId} from tenant admin`, person);
        person.contactEmail =
          person.contactEmail?.length == 0 ? undefined : person.contactEmail?.toString();
        let retCount = await personClient.tenantAdminSavePerson(person.portalId, person);

        this.isLoading = false;

        if (retCount > 0) {
          // Remove cache key now that a record is dirty
          this.lastSearch = "dirty";
          //no error means success
          return null;
        }
      } catch (apiError: any) {
        console.warn(`Error saving data for ${person.personId}`, apiError);
        this.isLoading = false;
        return apiError.response ?? apiError.message;
      }
      return "Save unsuccessful";
    },
    async selfSavePerson(person: SelfSavePersonCommand): Promise<string | null> {
      try {
        this.isLoading = true;

        console.log(`Saving person ${person.personId}`, person);
        person.contactEmail =
          person.contactEmail?.length == 0 ? undefined : person.contactEmail?.toString();
        let retCount = await personClient.selfSavePerson(person);

        this.isLoading = false;

        if (retCount > 0) {
          // Remove cache key now that a record is dirty
          this.lastSearch = "dirty";
          //no error means success
          return null;
        }
      } catch (apiError: any) {
        console.warn(`Error saving data for ${person.personId}`, apiError);
        this.isLoading = false;
        return apiError.response ?? apiError.message;
      }
      return "Save unsuccessful";
    },
    async searchPeople(
      portalId: number,
      searchString: string,
      excludeTestAccounts: boolean = false
    ): Promise<PeopleSearchDTO[] | null> {
      try {
        console.log(`Searching for '${searchString}'`);
        this.isLoading = true;
        var people = await personClient.getPeopleSearch(
          portalId,
          searchString,
          excludeTestAccounts
        );
        this.isLoading = false;
        return people;
      } catch (apiError: any) {
        console.warn(`Error searching for '${searchString}'`, apiError);
        this.isLoading = false;
      }
      return null;
    },
    async globalSearchPeople(searchString: string): Promise<GlobalPeopleSearchDTO[] | null> {
      try {
        console.log(`Searching for '${searchString}'`);
        this.isLoading = true;
        var people = await personClient.getGlobalPeopleSearch(searchString);
        this.isLoading = false;
        return people;
      } catch (apiError: any) {
        console.warn(`Error searching for '${searchString}'`, apiError);
        this.isLoading = false;
      }
      return null;
    },
    async deletePortalTestPeople(portalId: number): Promise<string> {
      try {
        await personClient.deletePortalTestPeople(portalId);
        return "";
      } catch (error: any) {
        console.error("Error deleting test people ", error);
        return error.response;
      }
    },
    async bulkAddPermissions(request: BulkAddPermissionsRequest): Promise<string> {
      try {
        await personClient.bulkAddPermissions(request);
        return "";
      } catch (apiError: any) {
        console.error("Error bulk adding permissions " + apiError);
        return apiError.response;
      }
    },
    async setPortalPermissions(request: SetPortalPermissionsRequest): Promise<string> {
      try {
        await personClient.setPortalPermissions(request);
        return "";
      } catch (apiError: any) {
        console.error("Error setting portal permissions " + apiError);
        return apiError.response;
      }
    },
    async deleteRegistration(personId: number, portalId: number): Promise<string | null> {
      this.isLoading = true;
      try {
        await personClient.deleteRegistration(personId, portalId);
        this.isLoading = false;
      } catch (apiError: any) {
        console.error("error while deleting registration", apiError.reponse);
        this.isLoading = false;
        return apiError?.response ?? apiError.message;
      }
      return null;
    },
    async tenantDeleteRegistration(personId: number, portalId: number): Promise<string | null> {
      this.isLoading = true;
      try {
        await personClient.tenantDeleteRegistration(personId, portalId);
        this.isLoading = false;
      } catch (apiError: any) {
        console.error("error while deleting registration", apiError.reponse);
        this.isLoading = false;
        return apiError?.response ?? apiError.message;
      }
      return null;
    },
    async deactivatePerson(personId: number, portalId: number): Promise<string | null> {
      this.isLoading = true;
      try {
        await personClient.deactivatePerson(personId, portalId);
        this.isLoading = false;
      } catch (apiError: any) {
        console.error("error while setting person as inactive", apiError.reponse);
        this.isLoading = false;
        return apiError?.response ?? apiError.message;
      }
      return null;
    },
    async tenantDeactivatePerson(personId: number, portalId: number): Promise<string | null> {
      this.isLoading = true;
      try {
        await personClient.tenantDeactivatePerson(personId, portalId);
        this.isLoading = false;
      } catch (apiError: any) {
        console.error("error whilesetting person as inactive", apiError.reponse);
        this.isLoading = false;
        return apiError?.response ?? apiError.message;
      }
      return null;
    },
    async reactivatePerson(personId: number, portalId: number): Promise<string | null> {
      this.isLoading = true;
      try {
        await personClient.reactivatePerson(personId, portalId);
        this.isLoading = false;
      } catch (apiError: any) {
        console.error("error while setting person as active", apiError.reponse);
        this.isLoading = false;
        return apiError?.response ?? apiError.message;
      }
      return null;
    },
    async tenantReactivatePerson(personId: number, portalId: number): Promise<string | null> {
      this.isLoading = true;
      try {
        await personClient.tenantReactivatePerson(personId, portalId);
        this.isLoading = false;
      } catch (apiError: any) {
        console.error("error whilesetting person as active", apiError.reponse);
        this.isLoading = false;
        return apiError?.response ?? apiError.message;
      }
      return null;
    },
  },
  getters: {},
});

//Enable Hot Module Replacement
// https://pinia.esm.dev/cookbook/hot-module-replacement.html
if ((import.meta as any).hot) {
  (import.meta as any).hot.accept(acceptHMRUpdate(usePersonStore as any, (import.meta as any).hot));
}
